/**
 * 用户 API
 * @module core/api/user
 */

import type { HttpClient } from '../http/HttpClient'
import type {
  UserInfo,
  VipInfo,
  PointsInfo,
  TempPointListParams,
  TempPointListResult,
  TempPointItem,
  PointsLogParams,
  PointsLogResult,
  Task,
  SignInResponse,
  FeedbackParams,
  FeedbackResponse,
  BindPhoneParams,
  TaskSchedule,
  ActivationCodeInfo
} from '../../types'
import { API_ENDPOINTS } from '../../constants/api-endpoints'
import { getSoftId, getWxLoginAndPayDomain } from '../config/SDKConfigManager'
import { stringifyQueryParams } from '../utils/url'

export class UserAPI {
  constructor(private httpClient: HttpClient) {}

  /** 获取用户信息 */
  async getUserInfo(): Promise<UserInfo> {
    const response = await this.httpClient.get(API_ENDPOINTS.USER.INFO)
    return response.data.data
  }

  /** 获取 VIP 信息 */
  async getVipInfo(): Promise<VipInfo> {
    const response = await this.httpClient.get(API_ENDPOINTS.USER.VIP)
    return response.data.data
  }

  /** 获取积分信息 */
  async getPointsInfo(): Promise<PointsInfo> {
    const response = await this.httpClient.get(API_ENDPOINTS.USER.POINTS)
    return response.data.data
  }

  /** 获取临时积分列表 */
  async getTempPointList(params: TempPointListParams = {}): Promise<TempPointListResult> {
    const query = stringifyQueryParams(params as Record<string, unknown>)
    const url = query ? `${API_ENDPOINTS.USER.TEMP_POINTS}?${query}` : API_ENDPOINTS.USER.TEMP_POINTS
    const response = await this.httpClient.get(url)
    return response.data.data
  }

  /** 获取单个临时积分记录详情 */
  async getTempPointDetail(id: number): Promise<TempPointItem> {
    const response = await this.httpClient.get(`${API_ENDPOINTS.USER.TEMP_POINTS}/${encodeURIComponent(String(id))}`)
    return response.data.data
  }

  /** 查询积分流水记录 */
  async getPointsLog(params: PointsLogParams = {}): Promise<PointsLogResult> {
    const {
      perPage,
      per_page,
      recentMonths,
      recent_months,
      sourceType,
      source_type,
      userPointId,
      user_point_id,
      ...rest
    } = params
    const query = stringifyQueryParams({
      ...rest,
      per_page: perPage ?? per_page,
      recent_months: recentMonths ?? recent_months,
      source_type: sourceType ?? source_type,
      user_point_id: userPointId ?? user_point_id
    })
    const url = query ? `${API_ENDPOINTS.USER.POINTS_LOG}?${query}` : API_ENDPOINTS.USER.POINTS_LOG
    const response = await this.httpClient.get(url)
    return response.data.data
  }

  /** 查询临时积分流水，固定 source_type=1 */
  async getTempPointLogs(params: PointsLogParams = {}): Promise<PointsLogResult> {
    return this.getPointsLog({ ...params, sourceType: 1 })
  }

  /** 获取任务列表 */
  async getTasks(): Promise<Task[]> {
    const response = await this.httpClient.get(API_ENDPOINTS.USER.TASKS)
    return response.data.data
  }

  /** 签到 */
  async signIn(): Promise<SignInResponse> {
    const response = await this.httpClient.post(API_ENDPOINTS.USER.SIGN_IN)
    return response.data
  }

  /** 提交反馈 */
  async feedback(params: FeedbackParams): Promise<FeedbackResponse> {
    const response = await this.httpClient.post(API_ENDPOINTS.USER.FEEDBACK, params)
    return response.data.data
  }

  /** 绑定手机号 */
  async bindPhone(params: BindPhoneParams): Promise<{ token?: string } | void> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.get(
      `${API_ENDPOINTS.USER.BIND_PHONE}?phone=${params.phone}&code=${params.code}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    // 检查业务错误码
    const code = response.data?.code
    if (code !== 0 && code !== 200) {
      const error = new Error(response.data?.msg || '绑定手机号失败') as Error & { code?: number }
      error.code = code
      throw error
    }
    return response.data.data
  }

  /** 绑定微信（获取二维码） */
  async bindWechat(): Promise<string> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.httpClient.post(
      API_ENDPOINTS.USER.BIND_WECHAT,
      { soft_id: getSoftId() },
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  /** 获取任务调度信息 */
  async getTaskSchedule(): Promise<TaskSchedule> {
    const response = await this.httpClient.post(API_ENDPOINTS.USER.TASK_SCHEDULE)
    return response.data.data
  }

  /** 获取激活码列表 */
  async getActivationCodeList(): Promise<ActivationCodeInfo[]> {
    const response = await this.httpClient.get(API_ENDPOINTS.USER.ACTIVATION_CODE_LIST)
    return response.data.data
  }

  /** 获取用户积分记录列表 */
  async getUserPointGrantList(params: Record<string, string | number>): Promise<any> {
    const url = `${API_ENDPOINTS.USER.POINT_GRANT_LIST}?${stringifyQueryParams(params)}`
    const response = await this.httpClient.get(url)
    return response.data.data
  }
}
